import Vue from 'vue';
import axios from 'axios';

import * as downloadsApi from '@motionelements/core/src/api/downloads.js';
import * as librariesApi from '@motionelements/core/src/api/libraries.js';
import * as favoritesApi from '@motionelements/core/src/api/favorites-core.js';
import * as historyApi from '@/api/history.js';
import * as elementsApi from '@/api/elements';
import * as foldersApi from '@/api/folders.js';
import allFilters from '@/views/library/sections/filters/filtersList.json';
// import notificationService from '@motionelements/core/src/services/notification.service.js';
// eslint-disable no-unused-vars

function refreshCancelToken(state, tokenName) {
  // cancel previous request
  if (_.has(state.cancelTokens, tokenName)) {
    const info = {
      type: 'cancelToken',
      name: `${tokenName}`,
    };
    state.cancelTokens[tokenName].cancel(info);
  }

  // create cancel token
  state.cancelTokens[tokenName] = axios.CancelToken.source();

  return state.cancelTokens[tokenName];
}

const state = {
  preferences: {
    pagination: {
      perPage: 10,
    },
    info: {
      name: true,
      category: true,
      price: true,
      // download content type
      date: true,
      tags: true,
    },
  },
  libraryContentType: 'download', // ['download', 'favorite', 'history', 'folder', 'member']
  // current folder
  currentFolder: null,
  currentFolderId: null,
  currentFolderLoading: false,
  // folders
  folders: [], // final
  folderNameMaxLength: 70,
  foldersLoading: false,
  showMoreFolder: false,
  //
  contentList: [],
  editingFolderInfo: {
    items: {
      totalCount: 0,
    },
  },
  isMobileSidebarActive: false,
  loading: {
    status: false,
  },
  loadingLoop: {
    status: false,
    message: '',
  },
  loadingProgress: {
    status: false,
    message: '',
    progress: 0,
  },
  allFilters,
  isNewFilter: false, // @toDo IT'S A Filter Toggler
  pagination: {
    totalCount: 0,
    currentPage: 1,
    // perPage: 20,
  },
  params: {
    sortBy: '',
    search: '',
    filters: [],
    selectedDownloadStatus: null,
    unorganized: false,
    published: '',
  },
  element: null,
  cancelTokens: {},
  member: null,
  productTypeCounts: [],
};

const getters = {
  getPreferences: state => state.preferences,
  selectedElementTypes: state => state.selectedElementTypes,
  selectedDownloadStatus: state => state.params.selectedDownloadStatus,
  libraryContentType: state => state.libraryContentType,
  currentFolderId: state => {
    if (state.currentFolderId) {
      return state.currentFolderId;
    }
    if (state.libraryContentType === 'favorite') {
      return 'default';
    }
  },
  contentList: state => state.contentList,
  showMoreFolder: state => state.showMoreFolder,
  editingFolderInfo: state => state.editingFolderInfo,
  getPerPage: state => state.preferences.pagination.perPage,
  getCurrentPage: state => state.pagination.currentPage,
  getTotalCount: state => state.pagination.totalCount,
  getTotalPageCount: state => Math.ceil(state.pagination.totalRows / state.preferences.pagination.perPage),
  getCurrentFolder: (state) => state.currentFolder,
  getParams({ preferences, pagination, params }) {
    return {
      page: pagination.currentPage,
      per_page: preferences.pagination.perPage,
      // todo : fix safari cache issue
      timestamp: new Date().getTime(),
      sort_by: params.sortBy,
      unorganized: params.unorganized,
      downloadable: params.selectedDownloadStatus,
    };
  },
  getQuery({ preferences, pagination, params }) {
    const queryObject = {};
    if (params.search) queryObject.q = params.search;
    if (pagination.currentPage) queryObject.page = pagination.currentPage;
    if (preferences.pagination.perPage) queryObject.per_page = preferences.pagination.perPage;
    if (params.sortBy) queryObject.sort_by = params.sortBy;
    if (params.unorganized) queryObject.unorganized = params.unorganized;
    if (params.published) queryObject.published = params.published;
    if (params.selectedDownloadStatus) queryObject.downloadable = params.selectedDownloadStatus;
    params.filters.forEach((filterItem) => {
      const filterKey = filterItem.id;
      queryObject[filterItem.id] = !queryObject[filterItem.id] ? [] : queryObject[filterItem.id];
      filterItem.filters.map(filter => `${filterItem.is ? '' : '-'}${filter}`).forEach((id) => {
        queryObject[filterKey].push(id);
      });
    });
    return queryObject;
  },
  //
  isFolderOwner({ currentFolder }, getters, rootState) {
    return _.get(currentFolder, 'owner.id') === _.get(rootState, 'user.member.id');
  },
  folderUrl(state) {
    const url = _.get(state, 'currentFolder.html.url');
    const shortUrl = _.get(state, 'currentFolder.html.shortUrl');
    return shortUrl ? shortUrl : url;
  },
};

const actions = {
  async getElement({ commit }, { id, params }) {
    const response = await elementsApi.getElement(id, params);
    commit('setElement', response.data.data);
  },
  getFavoritesInfo({ commit }) {
    const cancelToken = refreshCancelToken(state, 'getFavoritesInfo');
    const axiosConfig = {
      cancelToken: cancelToken.token,
    };
    return favoritesApi.getFavoritesInfo(axiosConfig)
      .then((response) => {
        // commit('setCurrentFolder', response.data);
        commit('setProductTypeCounts', response.data.productTypeCounts);
      })
      .catch(err => {
        console.error(err);
        // handle canceled by cancelToken
        if (axios.isCancel(err)) {
          console.log('request canceled', err);
        }
      });
  },
  // folder
  getFolder({ commit, state }, folderId) {
    const cancelToken = refreshCancelToken(state, 'getFolder');
    const axiosConfig = {
      cancelToken: cancelToken.token,
    };
    commit('setCurrentFolderLoading', true);
    commit('resetContentList');
    return foldersApi.getFolder(folderId, axiosConfig)
      .then(({ data: { data: folder } }) => {
        commit('setCurrentFolder', folder);
        commit('setProductTypeCounts', folder.productTypeCounts);
        commit('setCurrentFolderLoading', false);
      })
      .catch(err => {
        console.error(err);
        if (!axios.isCancel(err)) {
          commit('setCurrentFolderLoading', false);
        }
        throw new Error(err);
      });
  },
  listMemberFolders({ commit }, memberName) {
    commit('setLoading', { status: true });
    const cancelToken = refreshCancelToken(state, 'listMemberFolders');
    const axiosConfig = {
      cancelToken: cancelToken.token,
    };
    return librariesApi.getFolders(memberName, { per_page: 200 }, axiosConfig)
      .then(response => {
        commit('setFoldersLoading', false);
        commit('setFolders', response.data.data);
      })
      .catch(err => {
        console.error(err);
        commit('setFoldersLoading', false);
        throw new Error(err);
      })
      .finally(() => {
        commit('setLoading', { status: false });
      });
  },
  listLibraryFolders({ commit }) {
    // commit('setFoldersLoading', true);
    const cancelToken = refreshCancelToken(state, 'listLibraryFolders');
    const axiosConfig = {
      cancelToken: cancelToken.token,
    };
    return foldersApi.getLibraryFolders({ per_page: 200 }, axiosConfig)
      .then((response) => {
        commit('setFolders', response.data.data);
      })
      .catch((err) => {
        // handle error
        console.error('library/listLibraryFolders', err);
        // commit('setLoading', { status: false });
        if ([401, 403].includes(_.get(err, 'response.status', null))) {
          throw new Error();
        }
      })
      .finally(() => {
        // commit('setFoldersLoading', false);
      });
  },
  createFolder({ state }, { name }) {
    const params = {
      name,
    };
    return foldersApi.createFolder(params)
      .then(({ data: { data: folder } }) => {
        state.folders.unshift(folder);
      });
  },
  cloneFollowedMemberFolder(state, { name, clone }) { // clone is folderId
    return foldersApi.createFolder({ name, clone });
  },
  deleteFolder({ state }, folderId) {
    const folder = state.folders.find(x => x.id === folderId);
    state.folders.splice(state.folders.findIndex(x => x.id === folderId), 1);
    return foldersApi.deleteFolder(folderId)
      .catch(err => {
        console.error(err);
        state.folders.push(folder);
      });
  },
  updateFolder({ commit }, { folderId, folderName }) {
    const params = {
      name: folderName,
    };
    // update folder.name in store optimistically here first
    commit('updateFolder', { folderId, path: 'name', value: folderName });
    return foldersApi.updateFolder(folderId, params).then(() => {
      // auto refresh list
      // dispatch('listLibraryFolders');
    });
  },
  // eslint-disable-next-line no-unused-vars
  shareFolder({ commit }, info) {
    if (!_.size(info.folderId) || !_.size(info.emails)) {
      return false;
    }
    const folderId = info.folderId;
    const params = {
      'g-recaptcha-v3-response': info.recaptchaToken,
      emails: info.emails,
    };
    return foldersApi.shareFolder(folderId, params);
  },
  // contentList items
  LIST_DOWNLOADS_ITEMS({ state, commit, getters: { getParams, getQuery } }) {
    const cancelToken = refreshCancelToken(state, 'listItems');
    const axiosConfig = {
      cancelToken: cancelToken.token,
    };
    commit('setLoading', { status: true });
    commit('resetContentList');
    const params = { ...getParams, ...getQuery };
    return downloadsApi.getDownloads(params, axiosConfig)
      .then((response) => {
        console.log(response.data.data);
        //
        commit('setPagination', {
          currentPage: response.data.page,
          // perPage: response.data.perPage,
          totalCount: response.data.totalCount,
        });
        commit('setContentList', response.data.data);
        const mediaTypeFacets = _.get(response.data, 'facets.mediaType', []);
        if (mediaTypeFacets.length) {
          const productTypeCount = mediaTypeFacets.map(item => ({ productType: item.value, count: item.count }));
          commit('setProductTypeCounts', productTypeCount);
        }
        commit('setLoading', { status: false });
        // const products = response.data.data.map(download => download.product);
        // commit('setContentList', products);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) commit('setLoading', { status: false });
      });
  },
  listFavoritesItems({ commit, state, getters: { getParams, getQuery } }) {
    console.log(getParams);
    console.log(getQuery);
    const cancelToken = refreshCancelToken(state, 'listItems');
    const axiosConfig = {
      cancelToken: cancelToken.token,
    };
    commit('setLoading', { status: true });
    commit('resetContentList');
    getParams = { ...getParams, ...getQuery };
    return favoritesApi.listAllFavoriteItems(getParams, axiosConfig)
      .then((response) => {
        // set pagination
        commit('setPagination', {
          currentPage: response.data.page,
          // perPage: response.data.perPage,
          totalCount: response.data.totalCount,
        });
        commit('setContentList', response.data.data);
        commit('setLoading', { status: false });
      })
      .catch(e => {
        if (!axios.isCancel(e)) {
          commit('setLoading', { status: false });
        }
        // if (_.get(err, 'message.type') !== 'cancelToken') {
        //   commit('setLoading', { status: false });
        // }
      });
  },
  listFolderItems({ commit, state, getters: { getParams, getQuery } }, folderId) {
    const cancelToken = refreshCancelToken(state, 'listItems');
    const axiosConfig = {
      cancelToken: cancelToken.token,
    };
    commit('setLoading', { status: true });
    commit('resetContentList');
    commit('setCurrentFolderId', folderId);
    getParams = { ...getParams, ...getQuery };
    return foldersApi.listFolderItems(folderId, getParams, axiosConfig)
      .then((response) => {
        // response.data.data = response.data.data.map(x => ({ ...x, downloadable: true }));
        // set pagination
        commit('setPagination', {
          currentPage: response.data.page,
          // perPage: response.data.perPage,
          totalCount: response.data.totalCount,
        });
        commit('setContentList', response.data.data);
        commit('setLoading', { status: false });
      })
      .catch(e => {
        if (!axios.isCancel(e)) {
          commit('setLoading', { status: false });
        }
      });
  },
  listHistoryItems({ commit, state, getters: { getParams } }) {
    const cancelToken = refreshCancelToken(state, 'listItems');
    const axiosConfig = {
      cancelToken: cancelToken.token,
    };
    commit('setLoading', { status: true });
    commit('resetContentList');
    return historyApi.listHistoryItems('product-viewed', getParams, axiosConfig)
      .then((response) => {
        // set pagination
        commit('setPagination', {
          currentPage: response.data.page,
          // perPage: response.data.perPage,
          totalCount: response.data.totalCount,
        });
        // commit('setCurrentFolder', response.data);
        commit('setContentList', response.data.data);
      })
      .finally(() => {
        commit('setLoading', { status: false });
      });
  },
  copyFolderItems({ state, commit }, { folderId, skus }) {
    if (!skus.length) {
      return false;
    }
    commit('increaseFolderItems', { folderId, value: skus.length });
    return foldersApi.addFolderItems(folderId, skus)
      .then((response) => {
        console.log(response.data.data);
        const targetFolder = state.folders.find(f => f.id === folderId);
        if (targetFolder) targetFolder.items.totalCount = response.data.data.totalCount;
      });
  },
  moveFolderItems({ commit }, { fromFolderId, toFolderId, elements }) {
    if (!_.size(fromFolderId) || !_.size(toFolderId) || !_.size(elements)) {
      return false;
    }
    const params = {
      target_id: toFolderId,
      skus: elements,
    };
    state.contentList = state.contentList.filter(element => !elements.includes(element.id));
    commit('decreaseFolderItems', { folderId: fromFolderId, value: elements.length });
    commit('increaseFolderItems', { folderId: toFolderId, value: elements.length });
    commit('setLoading', { status: true });
    return foldersApi.moveFolderItems(fromFolderId, params);
  },
  removeFolderItems({ commit }, { skus, fromFolderId }) {
    if (!_.size(fromFolderId) || !_.size(skus)) {
      return false;
    }
    state.contentList = state.contentList.filter(item => !skus.includes(item.id));
    commit('decreaseFolderItems', { folderId: fromFolderId, value: skus.length });
    commit('setLoading', { status: true });
    return foldersApi.removeFolderItems(fromFolderId, { skus });
  },
  removeFavoriteItems({ commit }, { skus }) {
    if (!_.size(skus)) {
      return false;
    }
    state.contentList = state.contentList.filter(item => !skus.includes(item.id));
    commit('setLoading', { status: true });
    return favoritesApi.deleteFavoriteItems({ skus });
  },
  changePrivacy({ state }, privacy) {
    const cancelToken = refreshCancelToken(state, 'changePrivacy');
    const axiosConfig = {
      cancelToken: cancelToken.token,
    };
    if (privacy === 'private') {
      return foldersApi.unpublishFolder(state.currentFolder.id, axiosConfig);
    }
    return foldersApi.publishFolder(state.currentFolder.id, privacy, axiosConfig);
  },
  PIN_UNPIN_FOLDER_ITEM({ state: { currentFolderId } }, product) {
    // eslint-disable-next-line
    return product.pinned ? foldersApi.unpinFolderItem(currentFolderId, product.id) :
      foldersApi.pinFolderItem(currentFolderId, product.id);
  },
};

const mutations = {
  setMember(state, member) {
    state.member = member;
  },
  setPreferences(state, preferences) {
    state.preferences = preferences;
  },
  setElement(state, element) {
    state.element = element;
  },
  setSelectedElementTypes(state, elementTypes) {
    state.selectedElementTypes = elementTypes;
  },
  setLibraryContentType(state, listMode) {
    // state.pagination.currentPage = 1;
    state.libraryContentType = listMode;
  },
  setContentList(state, list) {
    state.contentList = list;
  },
  resetContentList(state) {
    state.contentList = [];
  },
  setMobileSidebarActive: (state, active) => {
    state.isMobileSidebarActive = active;
  },
  // current folder
  setCurrentFolder(state, folder) {
    state.currentFolder = folder;
    if (folder && folder.id) state.currentFolderId = folder.id;
  },
  setCurrentFolderId(state, folderId) {
    state.currentFolderId = folderId;
  },
  setCurrentFolderLoading(state, loading) {
    state.currentFolderLoading = loading;
  },
  // folders
  setFolders(state, list) {
    state.folders = list;
  },
  setFoldersLoading(state, status) {
    state.foldersLoading = status;
  },
  updateFolder({ folders, currentFolderId, currentFolder }, { folderId, path, value }) {
    const index = folders.findIndex(f => f.id === folderId);
    if (index > -1) {
      _.set(folders[index], path, value);
      if (folderId === currentFolderId && path === 'name') {
        _.set(currentFolder, path, value);
      }
    }
  },
  increaseFolderItems({ folders }, { folderId, value }) {
    const index = folders.findIndex(f => f.id === folderId);
    if (index > -1) folders[index].items.totalCount += value;
  },
  decreaseFolderItems({ folders }, { folderId, value }) {
    const index = folders.findIndex(f => f.id === folderId);
    if (index > -1) folders[index].items.totalCount -= value;
  },
  setShowMoreFolder(state, bool) {
    state.showMoreFolder = bool;
  },
  setEditingFolderInfo(state, info) {
    state.editingFolderInfo = info;
  },
  // loading
  setLoading: (state, params) => {
    state.loading.status = params.status;
  },
  setLoadingLoop: (state, params) => {
    state.loadingLoop.status = params.status;

    if (params.message) {
      state.loadingLoop.message = params.message;
    }
  },
  setLoadingProgress: (state, params) => {
    state.loadingProgress.status = params.status;

    if (params.message) {
      state.loadingProgress.message = params.message;
    }

    if (params.progress) {
      state.loadingProgress.progress = params.progress;
    }
  },
  // pagination
  setCurrentPage(state, number) {
    state.pagination.currentPage = number;
  },
  setPerPage(state, number) {
    state.preferences.pagination.perPage = +number;
  },
  setTotalCount(state, number) {
    state.pagination.totalCount = number;
  },
  setPagination(state, pagination) {
    state.pagination = pagination;
  },
  // ==== PARAMS ==== //
  updateParamsPublished({ params }, value) {
    params.published = value;
  },
  updateParamsSearch({ params }, value) {
    params.search = value;
  },
  updateParamsNotAny({ params }, value) {
    params.unorganized = value;
  },
  updateSortBy({ params }, value) {
    params.sortBy = value;
  },
  addFilterItem({ params: { filters } }, filter) {
    const filterItemIndex = filters.findIndex(item => item.id === filter.id);
    if (filterItemIndex < 0) {
      filters.push(filter);
    } else {
      this.commit('library/removeFilterItem', filter);
      filters.push(filter);
    }
  },
  removeFilterItem({ params: { filters } }, filterItem) {
    const index = filters.findIndex(({ id, is }) => id === filterItem.id && is === filterItem.is);
    if (index > -1) filters.splice(index, 1);
  },
  updateFilters({ params: { filters } }, filter) {
    const index = filters.findIndex(({ id }) => id === filter.id);
    const isIndex = filters.findIndex(({ id, is }) => id === filter.id && is);
    const isNotIndex = filters.findIndex(({ id, is }) => id === filter.id && !is);
    if (index === -1) {
      filters.push(filter);
    } else if (isIndex > -1 && isNotIndex < 0) {
      filter.is = false;
      filters.push(filter);
    } else if (isNotIndex > -1 && isIndex < 0) {
      filters.push(filter);
    }
  },
  resetParams(state) {
    state.params = {
      search: '',
      filters: [],
      sortBy: '',
      selectedDownloadStatus: null,
      unorganized: false,
      published: '',
    };
  },
  resetMediaTypeCounts(state) {
    state.productTypeCounts = [];
  },
  changeFilterItemStatus({ params: { filters } }, { value, index }) {
    filters[index].is = value;
  },
  updateFilterItemFilters({ params: { filters } }, { filter, index }) {
    const filterItem = filters[index];
    const filterIndex = filterItem.filters.findIndex(item => item === filter);
    if (filterIndex > -1) filterItem.filters.splice(filterIndex, 1);
    else filterItem.filters.push(filter.id);
  },
  setSelectedDownloadStatus(state, status) {
    state.params.selectedDownloadStatus = status;
  },
  // ==== END PARAMS ==== //
  // downloads
  updateDownloadAnnotation(state, { downloadId, annotations }) {
    // const targetDownload = state.contentList.find(download => download.order.id === orderId);
    const targetDownload = state.contentList.find(download => download.sku === downloadId);
    Vue.set(targetDownload, 'annotations', annotations);
  },
  setProductTypeCounts(state, array) {
    state.productTypeCounts = array;
  },
  //
  cancelRequest(state, tokenName) {
    if (_.has(state.cancelTokens, tokenName)) {
      const info = {
        type: 'cancelToken',
        name: `${tokenName}`,
      };
      state.cancelTokens[tokenName].cancel(info);
      console.log(`============ REQUEST ${tokenName} CANCELED `);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
